/**
 * Builds gateway options with tag filtering for RouteAmountSection.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState } from 'react';

import { LeadsListTags, NO_TAGS_VALUE } from '@/features/smswiser-campaigns/components/shared';

interface GatewayInput {
  id: number;
  name: string;
  pricePerSms?: number | null;
}

interface UseGatewayOptionsParams {
  gateways: GatewayInput[];
  tagsByGatewayId?: Record<string, string[]>;
  tagColors?: Record<string, string>;
}

function getTagsForRouteId(tagsByGatewayId: Record<string, string[]> | undefined, rid: number) {
  if (!tagsByGatewayId) return undefined;
  const direct = tagsByGatewayId[String(rid)];
  if (direct) return direct;
  const matchingKey = Object.keys(tagsByGatewayId).find(
    (k) => k.endsWith(`_${rid}`) || k === String(rid),
  );
  return matchingKey ? tagsByGatewayId[matchingKey] : undefined;
}

export function useGatewayOptions({ gateways, tagsByGatewayId, tagColors }: UseGatewayOptionsParams) {
  const [gatewayTagFilter, setGatewayTagFilter] = useState('');

  const availableGatewayTags = useMemo(() => {
    if (!tagsByGatewayId) return [];
    const set = new Set<string>();
    for (const tags of Object.values(tagsByGatewayId)) {
      for (const t of tags ?? []) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagsByGatewayId]);

  const gatewayOptions = useMemo(() => {
    const options = gateways.map((g) => {
      const tags = getTagsForRouteId(tagsByGatewayId, g.id);
      const base = `${g.name}${g.pricePerSms != null ? ` ($${Number(g.pricePerSms).toFixed(3)}/SMS)` : ''}`;
      const label = tags?.length ? `${base} ${tags.map((t) => `[${t}]`).join(' ')}` : base;
      const optionContent = tags?.length ? (
        <span className="flex flex-wrap items-center gap-1.5">
          <span>{base}</span>
          <LeadsListTags performanceTags={tags} tagColors={tagColors} compact />
        </span>
      ) : undefined;
      // The closed trigger is one h-10 line: there the wrapping card above
      // grew a second chip row that overflow-hidden chopped mid-letter. Name
      // truncates first, chips keep their shape and clip at the edge.
      const triggerContent = tags?.length ? (
        <span className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          <span className="min-w-0 flex-1 truncate">{base}</span>
          {/* One chip + the +N pill: the NAME is what identifies the pick —
              full chips live in the dropdown and in the +N hover. */}
          <LeadsListTags performanceTags={tags} tagColors={tagColors} compact maxVisible={1} />
        </span>
      ) : undefined;
      return { value: g.id, label, optionContent, triggerContent };
    });
    if (!gatewayTagFilter || !tagsByGatewayId) return options;
    if (gatewayTagFilter === NO_TAGS_VALUE) {
      return options.filter((o) => !getTagsForRouteId(tagsByGatewayId, o.value)?.length);
    }
    const q = gatewayTagFilter.toLowerCase();
    return options.filter((o) =>
      getTagsForRouteId(tagsByGatewayId, o.value)?.some((t) => t.toLowerCase() === q),
    );
  }, [gateways, gatewayTagFilter, tagsByGatewayId, tagColors]);

  return { gatewayOptions, gatewayTagFilter, setGatewayTagFilter, availableGatewayTags };
}
